import React from 'react';
import { Card } from '../common';

// Scope View Component - ALL SCOPES with Section-Level Compliance
const ScopeView = ({ data }) => {
    if (!data) return null;

    const getStatusColor = (status) => {
        if (!status) return "bg-gray-100 text-gray-600";
        if (status === "Compliant" || (status.includes("Aligned") && !status.includes("Not Aligned"))) {
            return "bg-green-100 text-green-700";
        }
        if (status === "Not Compliant" || status.includes("Not Aligned") || status.includes("Corrections Required")) {
            return "bg-red-100 text-red-700";
        }
        if (status.includes("Pending")) {
            return "bg-yellow-100 text-yellow-700";
        }
        return "bg-gray-100 text-gray-600";
    };

    const getSectionStatusBg = (status) => {
        if (status === "Compliant") return "bg-green-500";
        if (status === "Not Compliant") return "bg-red-500";
        return "bg-gray-400";
    };

    const hasIssues = (scope) => {
        if (scope.overall_status === "Not Compliant") return true;
        if (scope.discrepancies && scope.discrepancies.length > 0) return true;
        if (scope.sections) {
            return Object.values(scope.sections).some(s => s.status === "Not Compliant");
        }
        return false;
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
    };

    const sectionLabels = {
        scope: "Scope",
        price: "Price",
        inclusions: "Inclusions",
        exclusions: "Exclusions",
        material: "Material"
    };

    const getRiskBadgeColor = (risk) => {
        if (risk?.toLowerCase().includes('high')) return 'bg-red-100 text-red-800 border-red-300';
        if (risk?.toLowerCase().includes('med')) return 'bg-orange-100 text-orange-800 border-orange-300';
        return 'bg-green-100 text-green-800 border-green-300';
    };

    // Count stats
    const totalScopes = data.scopes_identified?.length || 0;
    const compliantScopes = data.scopes_identified?.filter(s => s.overall_status === "Compliant").length || 0;
    const outOfScopeCount = data.scopes_identified?.filter(s => s.overall_status === "Out of Scope").length || 0;
    const nonCompliantScopes = totalScopes - compliantScopes - outOfScopeCount;

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-900">Scope Review – Contract Admin</h2>
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(data.scope_review_status)}`}>
                    {data.scope_review_status || "Pending"}
                </span>
            </div>

            {/* Required Revisions */}
            {data.required_revisions?.length > 0 && (
                <div className="bg-white border-2 border-blue-200 rounded-lg overflow-hidden">
                    <div className="px-4 py-3 bg-blue-50 border-b border-blue-200">
                        <span className="font-bold text-blue-900">📝 Required Contract Revisions</span>
                    </div>
                    <div className="divide-y divide-gray-200">
                        {data.required_revisions.map((rev, i) => (
                            <div key={i} className="p-4 space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="font-bold text-gray-900">{i + 1}. {rev.title}</span>
                                    <span className={`px-2 py-1 text-xs font-bold rounded border ${getRiskBadgeColor(rev.risk_level)}`}>
                                        {rev.risk_level} Risk
                                    </span>
                                </div>
                                <div className="text-sm text-gray-700">{rev.issue}</div>
                                {rev.gc_ready_language && (
                                    <div className="bg-green-50 p-3 rounded border border-green-200">
                                        <div className="text-xs text-green-700 font-bold mb-1">Please Acknowledge:</div>
                                        <div className="text-sm text-green-900 italic">"{rev.gc_ready_language}"</div>
                                        <button 
                                            onClick={() => copyToClipboard(rev.gc_ready_language)}
                                            className="mt-2 text-xs text-green-600 hover:text-green-800 underline"
                                        >
                                            Copy to clipboard
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Review Mode Info */}
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                        <span className="text-gray-500">Review Mode:</span>
                        <span className="ml-2 font-medium text-gray-900">
                            {data.scope_review_mode === "proposal_only" ? "Proposal Only" : 
                             data.scope_review_mode === "proposal_and_contract" ? "Proposal + Contract Comparison" : 
                             "Awaiting Documents"}
                        </span>
                    </div>
                    <div>
                        <span className="text-gray-500">Total Scopes:</span>
                        <span className="ml-2 font-bold text-gray-900">{totalScopes}</span>
                    </div>
                    <div>
                        <span className="text-gray-500">Proposal:</span>
                        <span className="ml-2 font-medium text-green-600">{data.proposal_filename || "Uploaded"}</span>
                    </div>
                    <div className="flex gap-4">
                        <span className="text-green-600 font-medium">✓ {compliantScopes} Compliant</span>
                        {nonCompliantScopes > 0 && (
                            <span className="text-red-600 font-medium">✗ {nonCompliantScopes} Issues</span>
                        )}
                        {outOfScopeCount > 0 && (
                            <span className="text-gray-500 font-medium">○ {outOfScopeCount} Out of Scope</span>
                        )}
                    </div>
                    {data.contract_filename && (
                        <div className="col-span-2">
                            <span className="text-gray-500">Contract:</span>
                            <span className="ml-2 font-medium text-blue-600">{data.contract_filename}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Legend */}
            <div className="flex gap-4 text-xs">
                <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded bg-green-500"></div>
                    <span className="text-gray-600">Compliant</span>
                </div>
                <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded bg-red-500"></div>
                    <span className="text-gray-600">Not Compliant (Action Required)</span>
                </div>
            </div>

            {/* Scopes List */}
            <div className="space-y-6">
                {data.scopes_identified && data.scopes_identified.map((scope, idx) => (
                    <Card key={idx} className={`overflow-hidden border-2 ${hasIssues(scope) ? 'border-red-300' : 'border-green-300'}`}>
                        {/* Scope Header */}
                        <div className={`px-4 py-3 border-b flex justify-between items-center ${hasIssues(scope) ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                            <div className="flex flex-col">
                                <span className="font-bold text-gray-900">Scope: {scope.scope_name}</span>
                                {/* Show prices if available */}
                                {(scope.proposal_price || scope.contract_price) && (
                                    <div className="flex gap-4 text-xs mt-1">
                                        {scope.proposal_price && (
                                            <span className="text-green-700">Proposal: {scope.proposal_price}</span>
                                        )}
                                        {scope.contract_price && (
                                            <span className="text-blue-700">Contract: {scope.contract_price}</span>
                                        )}
                                    </div>
                                )}
                            </div>
                            <span className={`px-2 py-1 rounded text-xs font-bold ${hasIssues(scope) ? 'bg-red-600 text-white' : 'bg-green-600 text-white'}`}>
                                {scope.overall_status || (hasIssues(scope) ? "Not Compliant" : "Compliant")}
                            </span>
                        </div>
                        
                        {/* Section Status Grid */}
                        {scope.sections && (
                            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                                <div className="flex flex-wrap gap-2">
                                    {Object.entries(scope.sections).map(([key, section]) => (
                                        <div key={key} className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-2 ${
                                            section.status === "Compliant" ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                        }`}>
                                            <div className={`w-2 h-2 rounded-full ${getSectionStatusBg(section.status)}`}></div>
                                            {sectionLabels[key] || key}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        
                        {/* Discrepancies */}
                        {scope.discrepancies && scope.discrepancies.length > 0 && (
                            <div className="p-4 space-y-4">
                                <div className="text-sm font-bold text-red-700 mb-2">⚠️ Discrepancies Found ({scope.discrepancies.length})</div>
                                
                                {scope.discrepancies.map((disc, dIdx) => (
                                    <div key={dIdx} className="bg-red-50 p-4 rounded-lg border border-red-200 space-y-3">
                                        {/* Section Badge */}
                                        <div className="flex items-center gap-2">
                                            <span className="px-2 py-1 rounded text-xs font-bold bg-red-600 text-white">
                                                {disc.section}
                                            </span>
                                        </div>
                                        
                                        {/* Proposal Reference */}
                                        {disc.proposal_reference && (
                                            <div>
                                                <div className="text-xs text-green-600 uppercase tracking-wide font-semibold mb-1">Proposal Reference:</div>
                                                <div className="bg-green-50 p-2 rounded border border-green-200 text-sm text-gray-700 italic">
                                                    "{disc.proposal_reference}"
                                                </div>
                                            </div>
                                        )}
                                        
                                        {/* Contract Reference */}
                                        {disc.contract_reference && (
                                            <div>
                                                <div className="text-xs text-blue-600 uppercase tracking-wide font-semibold mb-1">Contract Reference:</div>
                                                <div className="bg-blue-50 p-2 rounded border border-blue-200 text-sm text-gray-700 italic">
                                                    "{disc.contract_reference}"
                                                </div>
                                            </div>
                                        )}
                                        
                                        {/* Comments */}
                                        {disc.comments && (
                                            <div>
                                                <div className="text-xs text-red-600 uppercase tracking-wide font-semibold mb-1">Comments:</div>
                                                <div className="bg-white p-2 rounded border border-red-200 text-sm text-gray-800">
                                                    {disc.comments}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                        
                        {/* No Discrepancies - All Green */}
                        {(!scope.discrepancies || scope.discrepancies.length === 0) && !hasIssues(scope) && (
                            <div className="p-4 text-center text-green-700">
                                <div className="text-sm font-medium">✓ All sections compliant - No action required</div>
                            </div>
                        )}
                    </Card>
                ))}
            </div>

            {/* No Scopes Message */}
            {(!data.scopes_identified || data.scopes_identified.length === 0) && (
                <div className="text-center py-8 text-gray-500">
                    <p>No scopes identified yet.</p>
                    <p className="text-sm mt-2">Upload a Proposal document to begin scope review.</p>
                </div>
            )}

            {/* Final Status */}
            <div className={`p-4 rounded-lg ${getStatusColor(data.scope_review_status)}`}>
                <div className="font-bold text-center">
                    {data.scope_review_status || "Scope Review Status: Pending"}
                </div>
            </div>
        </div>
    );
};

export default ScopeView;
